import React, { useState, createContext, useEffect, useRef } from 'react';
import sliderIcon from '../../assets/Icon/slider_icon.svg';
import AESOPlogo from '../../assets/Icon/AESOP-logo.svg';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import apiService from '../../services/apiService';
import ErrorMessagePopup from '../../CommanService/ErrorMessagePopup';
import Select from 'react-select';
import './CustomSelect.css';
import './ControlBar.css';
import Loader from '../../CommanService/Loader';
import { BatchExportQueueContext } from '../../contexts/BatchExportQueueContext';
import { apiCallWithCache, clearPendingRequests } from '../../utils/apiUtils';

export const MoveToPriceLineContext = React.createContext(false);
export const PriceLinePercentContext = React.createContext({});
export const FilteredInventoryContext = createContext({ filteredInventory: [], setFilteredInventory: () => { } });
export const FilteredSalesContext = React.createContext([]);
export const AllSalesDataContext = React.createContext([]);
export const AllInventoryDataContext = React.createContext([]);
export const AllStockDataContext = React.createContext([]);
export const SelectionContext = React.createContext({
  selectedRegion: '',
  selectedInterchange: '',
  selectedMlModel: null,
  selectedVariable: null,
  selectedGroup: null,
  selectedPartType: null,
  mlModels: [],
  tbVariables: [],
  tbGroups: [],
  regions: [],
  allApisLoaded: false,
  showSoldParts: true,
  includeBrokered: true
});
export const SelectedInventoryItemContext = createContext({ selectedInventoryItem: null, setSelectedInventoryItem: () => {} });
export const SelectedSalesItemContext = createContext({ selectedSalesItem: null, setSelectedSalesItem: () => {} });
export const SuggestedPriceContext = createContext({ suggestedPrice: null, setSuggestedPrice: () => {} });
export const PredictedPriceDataContext = createContext({ predictedPriceData: null, setPredictedPriceData: () => {} });

const ControlBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [moveToPriceLine, setMoveToPriceLine] = useState(false);
  const [enginePricing, setEnginePricing] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [sales, setSales] = useState([]);
  const [stock, setStock] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [interchanges, setInterchanges] = useState([]);
  const [selectedInterchange, setSelectedInterchange] = useState('');
  const [regionSearch, setRegionSearch] = useState("");
  const [interchangeSearch, setInterchangeSearch] = useState("");
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [selectedSalesItem, setSelectedSalesItem] = useState(null);
  const [priceLineConfig, setPriceLineConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // ML Model and Variables state
  const [mlModels, setMlModels] = useState([]);
  const [tbVariables, setTbVariables] = useState([]);
  const [tbGroups, setTbGroups] = useState([]);
  const [selectedMlModel, setSelectedMlModel] = useState(null);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Suggested Price state
  const [suggestedPrice, setSuggestedPrice] = useState(null);
  // State for part type selection
  const [selectedPartType, setSelectedPartType] = useState('');

  // Batch Export Queue state
  const [batchExportQueue, setBatchExportQueue] = useState([]);

  // Predicted Price from API state
  const [predictedPriceData, setPredictedPriceData] = useState(null);
  // Guard to ensure PridictPriceByBaseVariable is called only once at a time
  const isPricePredictRequestInFlight = useRef(false);

  // Checkbox states for sales data filtering
  const [showSoldParts, setShowSoldParts] = useState(true);
  const [includeBrokered, setIncludeBrokered] = useState(true);

  // Popup state for API errors (reuse existing ErrorMessagePopup)
  const [popupState, setPopupState] = useState({
    isVisible: false,
    message: '',
    subMessage: '',
    popupType: 'error',
    customPosition: 'validation'
  });
  const closePopup = () => setPopupState(prev => ({ ...prev, isVisible: false }));

  // File name popup state
  const [fileNamePopup, setFileNamePopup] = useState({
    isVisible: false,
    fileName: ''
  });
  const openFileNamePopup = () => setFileNamePopup(prev => ({ ...prev, isVisible: true }));
  const closeFileNamePopup = () => setFileNamePopup(prev => ({ ...prev, isVisible: false, fileName: '' }));

  // Save operation loading state
  const [saveLoading, setSaveLoading] = useState(false);

  // Loading states for individual APIs
  const [enginePricingLoading, setEnginePricingLoading] = useState(true);
  const [salesLoading, setSalesLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(true);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [mlModelsLoading, setMlModelsLoading] = useState(true);
  const [tbVariablesLoading, setTbVariablesLoading] = useState(true);
  const [priceLineConfigLoading, setPriceLineConfigLoading] = useState(true);

  // Ref to track if data has been loaded
  const dataLoadedRef = useRef(false);

  // All Analysis state
  const [allAnalysis, setAllAnalysis] = useState([]);
  const [analysisSearch, setAnalysisSearch] = useState('');
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showAnalysisDropdown, setShowAnalysisDropdown] = useState(false);

  useEffect(() => {
    GetAllAnalysisBYUSerID();
  }, []);

  // Get all analysis by user id
  const GetAllAnalysisBYUSerID = async () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const userId = user?.id || 'Not available';
    const response = await apiService.GetAllAnalysisBYUSerID(userId);
    setAllAnalysis(response.data);
    console.log('All Analysis:', allAnalysis);    
  };

  // Handle analysis search input change
  const handleAnalysisSearchChange = (e) => {
    const value = e.target.value;
    setAnalysisSearch(value);
    setShowAnalysisDropdown(true);
    if (!value) {
      setShowAnalysisDropdown(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowAnalysisDropdown(false);
    } else if (e.key === 'Enter' && filteredAnalysis.length > 0) {
      handleAnalysisSelect(filteredAnalysis[0]);
    }
  };

  // Handle analysis selection
  const handleAnalysisSelect = (analysis) => {
    setSelectedAnalysis(analysis);
    setAnalysisSearch(analysis.analysisName);
    setShowAnalysisDropdown(false);
    console.log('Selected Analysis:', analysis);
    
    // Load analysis data - you can implement this function to restore the analysis state
    loadAnalysisData(analysis);
  };

  // Load analysis data and restore state
  const loadAnalysisData = (analysis) => {
    // Set region and interchange from the selected analysis
    if (analysis.region) {
      setSelectedRegion(analysis.region);
    }
    if (analysis.interchange) {
      setSelectedInterchange(analysis.interchange);
    }
    
    // Set price line values
    if (analysis.price_Line_Q1 !== undefined) {
      setPriceLine1(Number(analysis.price_Line_Q1));
    }
    if (analysis.price_Line_Q2 !== undefined) {
      setPriceLine2(Number(analysis.price_Line_Q2));
    }
    if (analysis.price_Line_Q3 !== undefined) {
      setPriceLine3(Number(analysis.price_Line_Q3));
    }
    if (analysis.price_Line_Q4 !== undefined) {
      setPriceLine4(Number(analysis.price_Line_Q4));
    }
    
    // Set additional price line percent
    if (analysis.additional_Price_Line_Percent !== undefined) {
      setAdditionalPriceLine(Number(analysis.additional_Price_Line_Percent));
    }
    
    // Set user mileage
    if (analysis.user_Mileage !== undefined) {
      setUserMileage(analysis.user_Mileage.toString());
    }
    
    // Set checkboxes
    if (analysis.show_Sales !== undefined) {
      setShowSoldParts(Boolean(analysis.show_Sales));
    }
    if (analysis.include_Brokered !== undefined) {
      setIncludeBrokered(Boolean(analysis.include_Brokered));
    }
    
    // Set move to price line checkbox
    if (analysis.move_To_Price_Line !== undefined) {
      setMoveToPriceLine(Boolean(analysis.move_To_Price_Line));
    }
    
    console.log('Loading analysis data for:', analysis.analysisName);
    console.log('Patched control values:', {
      region: analysis.region,
      interchange: analysis.interchange,
      priceLine1: analysis.price_Line_Q1,
      priceLine2: analysis.price_Line_Q2,
      priceLine3: analysis.price_Line_Q3,
      priceLine4: analysis.price_Line_Q4,
      additionalPriceLine: analysis.additional_Price_Line_Percent,
      userMileage: analysis.user_Mileage,
      showSoldParts: analysis.show_Sales,
      includeBrokered: analysis.include_Brokered,
      moveToPriceLine: analysis.move_To_Price_Line
    });
  };

  // Clear analysis search
  const clearAnalysisSearch = () => {
    setAnalysisSearch('');
    setSelectedAnalysis(null);
    setShowAnalysisDropdown(false);
  };

  // Filter analysis based on search
  const filteredAnalysis = allAnalysis.filter(analysis =>
    analysis.analysisName.toLowerCase().includes(analysisSearch.toLowerCase())
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAnalysisDropdown && !event.target.closest('.analysis-dropdown-container')) {
        setShowAnalysisDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAnalysisDropdown]);
  

  // Consolidated useEffect to load all data at once
  useEffect(() => {
    if (dataLoadedRef.current) return; // Prevent multiple calls
    
    const loadAllData = async () => {
      try {
        console.log('Starting to load all data sequentially...');
        
        // Helper function to add delay between API calls
        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
        
        // Load data sequentially to prevent overwhelming the server
        try {
          console.log('Loading engine pricing...');
          const enginePricingResult = await apiCallWithCache('enginePricing', () => apiService.GetAllEnginePricing());
          if (enginePricingResult?.data) {
            setEnginePricing(enginePricingResult.data);
            const uniqueRegions = [...new Set(enginePricingResult.data.map(item => item.region))];
            setRegions(uniqueRegions);
            if (uniqueRegions.length > 0) {
              setSelectedRegion(uniqueRegions[0]);
            }
          }
        } catch (error) {
          console.error('Failed to load engine pricing:', error);
        }
        setEnginePricingLoading(false);
        await delay(1000); // Wait 1 second between API calls

        try {
          console.log('Loading sales data...');
          const salesResult = await apiCallWithCache('sales', () => apiService.GetAllsales());
          if (salesResult?.data) {
            
            let salesResultantData = salesResult?.data.map(item => ({
              ...item,
              mileage: item.mileage * 1000
            }));
            
            console.log('***Sales Data:', salesResultantData);
            setSalesData(salesResultantData);
            if (window.updateSalesData) {
              window.updateSalesData(salesResultantData);
            }
          }
        } catch (error) {
          console.error('Failed to load sales:', error);
        }
        setSalesLoading(false);
        await delay(1000);

        try {
          console.log('Loading stock data...');
          const stockResult = await apiCallWithCache('stock', () => apiService.GetAllStock());
          if (stockResult?.data) {
            setStock(stockResult.data);
          }
        } catch (error) {
          console.error('Failed to load stock:', error);
        }
        setStockLoading(false);
        await delay(1000);

        try {
          console.log('Loading inventory data...');
          const inventoryResult = await apiCallWithCache('inventory', () => apiService.GetAllInventory());
          if (inventoryResult?.data) {
            setInventory(inventoryResult.data);
          }
        } catch (error) {
          console.error('Failed to load inventory:', error);
        }
        setInventoryLoading(false);
        await delay(1000);

        try {
          console.log('Loading ML models...');
          const mlModelsResult = await apiCallWithCache('mlModels', () => apiService.GetAllTBModels());
          if (mlModelsResult?.data) {
            setMlModels(mlModelsResult.data);
          }
        } catch (error) {
          console.error('Failed to load ML models:', error);
        }
        setMlModelsLoading(false);
        await delay(1000);

        try {
          console.log('Loading TB groups...');
          const tbGroupsResult = await apiCallWithCache('tbGroups', () => apiService.GetAllTbGroup());
          if (tbGroupsResult?.data) {
            setTbGroups(tbGroupsResult.data);
          }
        } catch (error) {
          console.error('Failed to load TB groups:', error);
        }
        await delay(1000);

        try {
          console.log('Loading TB variables...');
          const tbVariablesResult = await apiCallWithCache('tbVariables', () => apiService.GetAllTBVariables());
          if (tbVariablesResult?.data) {
            setTbVariables(tbVariablesResult.data);
          }
        } catch (error) {
          console.error('Failed to load TB variables:', error);
        }
        setTbVariablesLoading(false);
        await delay(1000);

        try {
          console.log('Loading price line config...');
          const priceLineConfigResult = await apiCallWithCache('priceLineConfig', () => apiService.GetPriceLineConfig());
          if (priceLineConfigResult?.data) {
            setPriceLineConfig(priceLineConfigResult.data);
          }
        } catch (error) {
          console.error('Failed to load price line config:', error);
        }
        setPriceLineConfigLoading(false);

        setLoading(false);
        dataLoadedRef.current = true;
        console.log('All data loaded successfully');
        
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
        // Set all loading states to false on error
        setEnginePricingLoading(false);
        setSalesLoading(false);
        setStockLoading(false);
        setInventoryLoading(false);
        setMlModelsLoading(false);
        setTbVariablesLoading(false);
        setPriceLineConfigLoading(false);
      }
    };

    loadAllData();
    
    // Cleanup function to clear pending requests when component unmounts
    return () => {
      clearPendingRequests();
    };
  }, []); // Empty dependency array - only run once

  // Handle Region selection with PredictedData clearing
  const handleRegionChange = (opt) => {
    setSelectedRegion(opt ? opt.value : '');
    // Clear mileage input and predicted price data when region changes
    setUserMileage('');
    setPredictedPriceData(null);
    // Clear PredictedData array when region changes
    if (window.updatePredictedData) {
      window.updatePredictedData([]);
    }
  };

  // Handle Interchange selection with PredictedData clearing
  const handleInterchangeChange = (opt) => {
    setSelectedInterchange(opt ? opt.value : '');
    // Clear mileage input and predicted price data when interchange changes
    setUserMileage('');
    setPredictedPriceData(null);
    // Clear PredictedData array when interchange changes
    if (window.updatePredictedData) {
      window.updatePredictedData([]);
    }
  };

  // 6. Event listeners for dropdown selections
  useEffect(() => {
    const handleMlModelSelected = (event) => {
      setSelectedMlModel(event.detail);
      // Clear mileage input and predicted price data when ML model changes
      setUserMileage('');
      setPredictedPriceData(null);
    };

    const handleVariableSelected = (event) => {
      setSelectedVariable(event.detail);
      // Clear mileage input and predicted price data when variable changes
      setUserMileage('');
      setPredictedPriceData(null);
    };

    const handlePartTypeSelected = (event) => {
      setSelectedPartType(event.detail);
      // Clear mileage input and predicted price data when variable changes
      setUserMileage('');
      setPredictedPriceData(null);
    };
    const handleGroupSelected = (event) => {
      setSelectedGroup(event.detail);
      // Clear mileage input and predicted price data when group changes
      setUserMileage('');
      setPredictedPriceData(null);
    };

    window.addEventListener('mlModelSelected', handleMlModelSelected);
    window.addEventListener('variableSelected', handleVariableSelected);
    window.addEventListener('parttypeSelected', handlePartTypeSelected);
    window.addEventListener('groupSelected', handleGroupSelected);
    
    return () => {
      window.removeEventListener('mlModelSelected', handleMlModelSelected);
      window.removeEventListener('parttypeSelected', handlePartTypeSelected);
      window.removeEventListener('variableSelected', handleVariableSelected);
      window.removeEventListener('groupSelected', handleGroupSelected);
    };
  }, []);


  
    // Function to save and log all control values
  const AnalysiSave = async (fileName) => {
    // Get userId from session storage
    const user = JSON.parse(sessionStorage.getItem('user'));
    const userId = user?.id || 'Not available';
    const userName = user?.first_name + ' ' + user?.last_name;
    
    const allControlValues = {
      analysisName: fileName || "Test_Analysis", // Use the passed file name or fallback to default
      User_Id: userId ? userId.toString() : 'Not available',
      User_Name: userName || 'Not available',
      Price_Line_Q3: Number(priceLine3) || 0,
      Price_Line_Q4: Number(priceLine4) || 0,
      Price_Line_Q2: Number(priceLine2) || 0,
      Region: selectedRegion || '',
      Additional_Price_Line_Percent: Number(additionalPriceLine) || 0,
      Interchange: selectedInterchange || '',
      Price_Line_Q1: Number(priceLine1) || 0,
      Move_To_Price_Line: moveToPriceLine ? 1 : 0, // Convert boolean to decimal (1 or 0)
      Show_Sales: showSoldParts,
      User_Mileage: Number(userMileage) || 0, // Convert string to decimal
      Include_Brokered: includeBrokered,
    };
    try {
      const response = await apiService.SaveAnalysis(allControlValues);
      console.log('Analysis saved successfully:', response);
      GetAllAnalysisBYUSerID();
    } catch (error) {
      console.error('Error saving analysis:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
    }
  };

  // Function to open file name popup
  const PopupFileSave = () => {
    openFileNamePopup();
  };

  // Function to handle proceed action
  const handleProceed = async () => {
    if (!fileNamePopup.fileName.trim()) {
      // Show error if file name is empty
      setPopupState({
        isVisible: true,
        message: 'File name required',
        subMessage: 'Please enter a file name to proceed',
        popupType: 'error',
        customPosition: 'validation'
      });
      return;
    }
    
    // Close the popup
    closeFileNamePopup();
    
    // Call the actual save function with the file name
    await AnalysiSave(fileNamePopup.fileName);
  };
    
  


  // Function to handle input focus removal
  const handleInputBlur = async () => {
    // Prevent duplicate/concurrent calls
    if (isPricePredictRequestInFlight.current) return;
    
    // Check if required parameters have values
    if (!selectedMlModel?.value || !selectedVariable?.value || !userMileage) {
      console.log('API call prevented - missing required parameters:', {
        "ml_model": selectedMlModel?.value || "null",
        "variable": selectedVariable?.value || "null",
        "part_type": selectedPartType || "null",
        "input": userMileage || "null"
      });
      return;
    }
    
    isPricePredictRequestInFlight.current = true;
  
    // Call GetPricePredictingR API
    try {
      const requestData = {
        "ml_model": selectedMlModel?.value || "",
        "variable": selectedVariable?.value || "",
        "part_type": selectedPartType || "",
        "input": userMileage || ""
      };

      const response = await apiService.GetPricePredictingR(requestData);
      console.log('API Response:', response);

      // Show popup on failure or missing data; otherwise store the data
      const successFlag = response?.success ?? response?.Success ?? response?.status ?? false;
      const dataPayload = response?.data;
      const isEmptyArray = Array.isArray(dataPayload) && dataPayload.length === 0;
      const isNoData = dataPayload === null || dataPayload === undefined || isEmptyArray;

      if (!successFlag || isNoData) {
        setPredictedPriceData(null);
        setPopupState({
          isVisible: true,
          message: 'Prediction failed',
          subMessage: response?.message || 'Predictor not exists for given ML Model and Variable',
          popupType: 'error',
          customPosition: 'validation'
        });
      } else {
        setPredictedPriceData(dataPayload);
        console.log('Predicted Price Data stored:', dataPayload);
      }
    } catch (error) {
      console.error('Error calling GetPricePredictingR API:', error);
      setPredictedPriceData(null);
      setPopupState({
        isVisible: true,
        message: 'Prediction failed',
        subMessage: error.response?.data?.message || error.message || 'An error occurred while predicting price',
        popupType: 'error',
        customPosition: 'validation'
      });
    } finally {
      isPricePredictRequestInFlight.current = false;
    }
  };

  // Function to reset price line values to global defaults
  const resetPriceLineToDefaults = () => {
    if (selectedRegion && selectedInterchange && stock.length > 0) {
      const matchingStock = stock.filter(item => 
        item.region === selectedRegion && 
        item.interchange_number === selectedInterchange
      );

      console.log('Resetting Price Line Values - Matching Objects:', matchingStock);
      console.log('Total Matching Objects:', matchingStock.length);

      if (priceLineConfig && priceLineConfig.length > 0) {
        let matchingQuartiles = [];
        if (matchingStock.length > 0) {
          const firstStockObj = matchingStock[0];
          const activePartType = (selectedPartType || 'ENGINE');
          matchingQuartiles = priceLineConfig.filter(
            config => config.stock_status === firstStockObj.stock_status &&
                      String(config.part_type).toUpperCase() === String(activePartType).toUpperCase()
          );
        }
        // If no matching stock or no quartiles found, default to 'Adequately Stocked'
        if (matchingQuartiles.length < 4) {
          const activePartType = (selectedPartType || 'ENGINE');
          matchingQuartiles = priceLineConfig.filter(
            config => config.stock_status === 'Adequately Stocked' &&
                      String(config.part_type).toUpperCase() === String(activePartType).toUpperCase()
          );
        }
        // Sort and patch as before
        if (matchingQuartiles.length >= 4) {
          const sortedQuartiles = matchingQuartiles.sort((a, b) => {
            const order = { 'Q1': 0, 'Q2': 1, 'Q3': 2, 'Q4': 3 };
            return order[a.quartile_name] - order[b.quartile_name];
          });
          setPriceLine1(sortedQuartiles[0]?.quartile_value || 0);
          setPriceLine2(sortedQuartiles[1]?.quartile_value || 0);
          setPriceLine3(sortedQuartiles[2]?.quartile_value || 0);
          setPriceLine4(sortedQuartiles[3]?.quartile_value || 0);
          console.log('Reset Price Line Values to Defaults:', {
            Q1: sortedQuartiles[0]?.quartile_value,
            Q2: sortedQuartiles[1]?.quartile_value,
            Q3: sortedQuartiles[2]?.quartile_value,
            Q4: sortedQuartiles[3]?.quartile_value
          });
        }
      }
    }
  };

  // Filter and log matching stock data when region or interchange changes
  useEffect(() => {
    
    if (selectedRegion && selectedInterchange && stock.length > 0) {
      const matchingStock = stock.filter(item => 
        item.region === selectedRegion && 
        item.interchange_number === selectedInterchange
      );

      
      console.log('Matching Objects:', matchingStock);
      console.log('Total Matching Objects:', matchingStock.length);

      if (priceLineConfig && priceLineConfig.length > 0) {
        let matchingQuartiles = [];
        if (matchingStock.length > 0) {
          const firstStockObj = matchingStock[0];
          const activePartType = (selectedPartType || 'ENGINE');
          matchingQuartiles = priceLineConfig.filter(
            config => config.stock_status === firstStockObj.stock_status &&
                      String(config.part_type).toUpperCase() === String(activePartType).toUpperCase()
          );
        }
        // If no matching stock or no quartiles found, default to 'Adequately Stocked'
        if (matchingQuartiles.length < 4) {
          const activePartType = (selectedPartType || 'ENGINE');
          matchingQuartiles = priceLineConfig.filter(
            config => config.stock_status === 'Adequately Stocked' &&
                      String(config.part_type).toUpperCase() === String(activePartType).toUpperCase()
          );
        }
        // Sort and patch as before
        if (matchingQuartiles.length >= 4) {
          const sortedQuartiles = matchingQuartiles.sort((a, b) => {
            const order = { 'Q1': 0, 'Q2': 1, 'Q3': 2, 'Q4': 3 };
            return order[a.quartile_name] - order[b.quartile_name];
          });
          setPriceLine1(sortedQuartiles[0]?.quartile_value || 0);
          setPriceLine2(sortedQuartiles[1]?.quartile_value || 0);
          setPriceLine3(sortedQuartiles[2]?.quartile_value || 0);
          setPriceLine4(sortedQuartiles[3]?.quartile_value || 0);
          console.log('Patched Price Line Values:', {
            Q1: sortedQuartiles[0]?.quartile_value,
            Q2: sortedQuartiles[1]?.quartile_value,
            Q3: sortedQuartiles[2]?.quartile_value,
            Q4: sortedQuartiles[3]?.quartile_value
          });
        }
      }
    }
  }, [selectedRegion, selectedInterchange, stock, priceLineConfig, selectedPartType]);



  
  React.useEffect(() => {
    if (selectedRegion) {
      // Filter interchanges by selected region
      const filtered = enginePricing.filter(item => item.region === selectedRegion);
      setInterchanges(filtered.map(item => item.interchange_number));
      setSelectedInterchange('');
    } else {
      setInterchanges([]);
      setSelectedInterchange('');
    }
  }, [selectedRegion, enginePricing]);

  useEffect(() => {
    if (selectedRegion && selectedInterchange) {
      const filtered = inventory.filter(
        item => item.region === selectedRegion && item.interchange_number === selectedInterchange
      );
      setFilteredInventory(filtered);
    } else {
      setFilteredInventory([]);
    }
  }, [selectedRegion, selectedInterchange, enginePricing]);

  useEffect(() => {
    if (selectedRegion && selectedInterchange) {
      setFilteredSales(
        salesData.filter(
          sale =>
            sale.region === selectedRegion &&
            sale.interchange_number === selectedInterchange
        )
      );
    } else {
      setFilteredSales([]);
    }
  }, [selectedRegion, selectedInterchange, salesData]);

  // Filtered regions and interchanges based on search
  const filteredRegions = regions.filter(region => region.toLowerCase().includes(regionSearch.toLowerCase()));
  const filteredInterchanges = interchanges.filter(interchange => interchange.toString().includes(interchangeSearch));

  const regionOptions = regions.map(region => ({ value: region, label: region }));
  const interchangeOptions = interchanges.map(interchange => ({ value: interchange, label: interchange }));

  // 1. Use actual sales data for quartile calculation, filtered by selected region and interchange
  let MileageFromSalesData = [];
  if (salesData.length > 0 && selectedRegion && selectedInterchange) {
    MileageFromSalesData = salesData
      .filter(item => 
        item.region === selectedRegion && 
        item.interchange_number === selectedInterchange
      )
      .map(item => ({ mileage: item.mileage }));
  }

  // 2. Calculate mileage quartiles
  function getQuartiles(data) {
    if (!data || data.length === 0) return [0, 0, 0, 0, 0];
    const sorted = data.map(d => d.mileage).sort((a, b) => a - b);
    const q = p => {
      const pos = (sorted.length - 1) * p;
      const base = Math.floor(pos);
      const rest = pos - base;
      if (sorted[base + 1] !== undefined) {
        return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
      } else {
        return sorted[base];
      }
    };
    return [q(0), q(0.25), q(0.5), q(0.75), q(1)];
  }
  const quartiles = getQuartiles(MileageFromSalesData);

  // 3. Generate dynamic labels for the 4 Price Line % fields
  const priceLineLabels = [
    `Price Line % (${quartiles[0].toLocaleString()}-${quartiles[1].toLocaleString()} mi)`,
    `Price Line % (${quartiles[1].toLocaleString()}-${quartiles[2].toLocaleString()} mi)`,
    `Price Line % (${quartiles[2].toLocaleString()}-${quartiles[3].toLocaleString()} mi)`,
    `Price Line % (${quartiles[3].toLocaleString()}+ mi)`
  ];

  // 4. Set static stock status and default values for price line %
  const staticStockStatus = 'adequately stocked';
  const [priceLine1, setPriceLine1] = useState(0);
  const [priceLine2, setPriceLine2] = useState(0);
  const [priceLine3, setPriceLine3] = useState(0);
  const [priceLine4, setPriceLine4] = useState(0);
  const [additionalPriceLine, setAdditionalPriceLine] = useState(0);
  const [userMileage, setUserMileage] = useState('');


  // Check if all APIs are loaded
  const allApisLoaded = !enginePricingLoading && !salesLoading && !stockLoading && 
                       !inventoryLoading && !mlModelsLoading && !tbVariablesLoading && 
                       !priceLineConfigLoading;

  const priceLinePercentContextValue = {
    priceLine1, priceLine2, priceLine3, priceLine4, additionalPriceLine, userMileage
  };

  const tabs = [
    { label: 'main', path: '/control-bar/main' },
    { label: 'part detail', path: '/control-bar/part-detail' },
    { label: 'sales detail', path: '/control-bar/sales-detail' },
    { label: 'all interchanges', path: '/control-bar/all-interchanges' },
    // { label: 'inventory alerts', path: '/control-bar/inventory-alerts' }, // COMMENTED OUT: Inventory alerts menu
    { label: 'price line % config', path: '/control-bar/price-line-config' },
    // { label: 'batch export queue', path: '/control-bar/batch-export-queue' }, // COMMENTED OUT: Batch export queue tab
  ];

  return (
    <MoveToPriceLineContext.Provider value={moveToPriceLine}>
      <PriceLinePercentContext.Provider value={priceLinePercentContextValue}>
        <FilteredSalesContext.Provider value={filteredSales}>
                  <AllSalesDataContext.Provider value={salesData}>
          <AllInventoryDataContext.Provider value={inventory}>
            <AllStockDataContext.Provider value={stock}>
              <FilteredInventoryContext.Provider value={{ filteredInventory, setFilteredInventory }}>
                <SelectionContext.Provider value={{
                  selectedRegion,
                  selectedInterchange,
                  selectedMlModel,
                  selectedVariable,
                  selectedGroup,
                  selectedPartType,
                  mlModels,
                  tbVariables,
                  tbGroups,
                  regions,
                  allApisLoaded,
                  showSoldParts,
                  includeBrokered
                }}>
                                     <SuggestedPriceContext.Provider value={{ suggestedPrice, setSuggestedPrice }}>
                     <PredictedPriceDataContext.Provider value={{ predictedPriceData, setPredictedPriceData }}>
                       <BatchExportQueueContext.Provider value={{ batchExportQueue, setBatchExportQueue }}>
                                             <div className="main-container">
                      <ErrorMessagePopup
                        message={popupState.message}
                        subMessage={popupState.subMessage}
                        isVisible={popupState.isVisible}
                        onClose={closePopup}
                        customPosition={popupState.customPosition}
                        popupType={popupState.popupType}
                      />
                      {/* Loader overlay */}
                      {!allApisLoaded && (
                        <div className="loader-overlay">
                          <div className="bouncing-loader">
                            <div></div><div></div><div></div>
                          </div>
                          {/* <div style={{ marginTop: '20px', fontSize: '16px', color: '666' }}>
                            Loading data...
                          </div> */}
                          
                        </div>
                      )}
                  {/* COMMENTED OUT: Search analysis bar */}
                  <div className="control_header_blk d-flex align-items-center justify-content-between">
                    <h5 className='font_14 fw-semi text_black'>Search here to get your previous Analysis</h5>
                    <div className="d-flex align-items-center">
                      <label className="font_14 fw-med text_black mr_5 d-block">Filter by:</label>
                      <div className="position-relative analysis-dropdown-container">
                        <input 
                          placeholder="Search Analysis" 
                          className="form-control" 
                          value={analysisSearch}
                          onChange={handleAnalysisSearchChange}
                          onFocus={() => setShowAnalysisDropdown(true)}
                          onKeyDown={handleKeyDown}
                        />
                        {analysisSearch && (
                          <button 
                            type="button" 
                            className="clear-search-btn"
                            onClick={clearAnalysisSearch}
                            title="Clear search"
                          >
                            ×
                          </button>
                        )}
                        {showAnalysisDropdown && (
                          <div className="analysis-dropdown">
                            {filteredAnalysis.length > 0 ? (
                              filteredAnalysis.map((analysis, index) => (
                                <div 
                                  key={analysis.sessionId || index}
                                  className="analysis-dropdown-item"
                                  onClick={() => handleAnalysisSelect(analysis)}
                                >
                                  {analysis.analysisName}
                                </div>
                              ))
                            ) : (
                              <div className="analysis-dropdown-item no-results">
                                No analysis found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                 
                  <div className="main_page_blk d-flex align-items-start">
                    <aside className="control_sidemenu_blk">
                      <div className="contorl_blk d-flex align-items-center">
                        <img src={sliderIcon} alt="Controls" className="mr_5" />
                        <span>Controls</span>
                      </div>
                      <div className="aside_items_container">
                        {/* COMMENTED OUT: Clear and Save buttons */}
                        <div className="d-flex align-items-center justify-content-between mb_15">
                          <button className="clear_btn">Clear</button>
                          <button className="clear_btn save_btn" onClick={PopupFileSave}>Save</button>
                        </div>
                       
                        <label className="font_14 fw-semi text_white mb_5 d-block">Select Region</label>
                        <div className="position-relative mb_30">
                          <Select
                            options={regionOptions}
                            value={regionOptions.find(opt => opt.value === selectedRegion) || null}
                            onChange={handleRegionChange}
                            placeholder=""
                            isClearable
                            classNamePrefix="react-select"
                          />
                        </div>
                        <label className="font_14 fw-semi text_white mb_5 d-block">Select Interchange</label>
                        <div className="position-relative mb_30">
                          <Select
                            options={interchangeOptions}
                            value={interchangeOptions.find(opt => opt.value === selectedInterchange) || null}
                            onChange={handleInterchangeChange}
                            placeholder=""
                            isClearable
                            isDisabled={!selectedRegion}
                            classNamePrefix="react-select"
                          />
                        </div>
                        {/* <label className="font_14 fw-semi text_white mb_5 d-block">Enter Custom Value (to filter the data)</label>
                        <input className="form-control w-100 mb_15" /> */}
                        <div className="d-flex align-items-center mb_30">
                          <input type="checkbox" className="mr_5" checked={showSoldParts} onChange={e => setShowSoldParts(e.target.checked)} />
                          <span className='font_14 fw-med text_white'>Show Sold Parts on Chart</span>
                        </div>
                        <div className="d-flex align-items-center mb_15">
                          <input 
                            type="checkbox" 
                            className={`mr_5 ${showSoldParts ? 'checkbox-enabled' : 'checkbox-disabled'}`}
                            checked={includeBrokered} 
                            onChange={e => setIncludeBrokered(e.target.checked)}
                            disabled={!showSoldParts}
                          />
                          <span className={`font_14 fw-med text_white ${showSoldParts ? 'text-enabled' : 'text-disabled'}`}>Include Brokered Sales</span>
                        </div>
                        <label className="font_14 fw-semi text_white mb_5 d-block">Enter Mileage (mi)</label>
                        <input 
                          type="number"
                          className="form-control w-100 mb_30" 
                          value={userMileage}
                          onChange={e => setUserMileage(e.target.value)}
                          onBlur={handleInputBlur}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              // Trigger blur to ensure only one call (onBlur handler)
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder=""
                          min="0"
                         
                          step="1"
                         disabled={!selectedMlModel?.value || !selectedVariable?.value}
                        />
                        <label className="font_14 fw-semi text_white mb_5 d-block">Additional Price Line % (Add to Quartile %)</label>
                        <input className="form-control w-100 mb_15" value={additionalPriceLine} onChange={e => setAdditionalPriceLine(Number(e.target.value))} />
                        <div className="d-flex align-items-center mb_15">
                          <input type="checkbox" className="mr_5" checked={moveToPriceLine} onChange={e => setMoveToPriceLine(e.target.checked)} />
                          <span className='font_14 fw-med text_white'>Move Inventory to Price Line</span>
                        </div>
                        <button className="clear_btn filter_btn mb_15" onClick={() => {
                          if (window.handleQueuePriceChanges) {
                            window.handleQueuePriceChanges();
                          }
                          // Restore data if it was previously cleared
                          if (window.restoreBatchExportData) {
                            window.restoreBatchExportData();
                          }
                        }}>Queue Price Changes</button>
                        {/* Dynamic Price Line % fields with quartile-based labels and editable values */}
                        {(() => {
                          if (selectedRegion && selectedInterchange) {
                            return (
                              <>
                                <label className="font_14 fw-semi text_white mb_5 d-block">{priceLineLabels[0]}</label>
                                <input className="form-control w-100 mb_15" value={priceLine1} onChange={e => setPriceLine1(Number(e.target.value))} />
                                <label className="font_14 fw-semi text_white mb_5 d-block">{priceLineLabels[1]}</label>
                                <input className="form-control w-100 mb_15" value={priceLine2} onChange={e => setPriceLine2(Number(e.target.value))} />
                                <label className="font_14 fw-semi text_white mb_5 d-block">{priceLineLabels[2]}</label>
                                <input className="form-control w-100 mb_15" value={priceLine3} onChange={e => setPriceLine3(Number(e.target.value))} />
                                <label className="font_14 fw-semi text_white mb_5 d-block">{priceLineLabels[3]}</label>
                                <input className="form-control w-100 mb_15" value={priceLine4} onChange={e => setPriceLine4(Number(e.target.value))} />
                                <button className="clear_btn filter_btn mb_15" onClick={resetPriceLineToDefaults}>Reset to Global Defaults</button>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </aside>
                    <div className="control_main_item_container">
                      <div className="d-flex align-items-center control_logo_blk">
                        <img src={AESOPlogo} alt="logo" className="h_30px mr_10" />
                        <span className="font_18 fw-semi text_white">Pricing App</span>
                      </div>
                      {/* Tab Bar in main content area */}
                      <div className="tab-bar control_tab_container d-flex align-items-start">
                        {tabs.map(tab => (
                          <button
                            key={tab.path}
                            className={location.pathname === tab.path ? 'tab active' : 'tab'}
                            onClick={() => navigate(tab.path)}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                        <SelectedInventoryItemContext.Provider value={{ selectedInventoryItem, setSelectedInventoryItem }}>
                          <SelectedSalesItemContext.Provider value={{ selectedSalesItem, setSelectedSalesItem }}>
                          <Outlet />
                          </SelectedSalesItemContext.Provider>
                        </SelectedInventoryItemContext.Provider>
                    </div>
                  </div>
                      </div>

                      {/* File Name Popup */}
                      {fileNamePopup.isVisible && (
                        <div className="file-name-popup-overlay" onClick={closeFileNamePopup}>
                          <div className="file-name-popup" onClick={(e) => e.stopPropagation()}>
                            <div className="file-name-popup-header">
                              <h5>Save Analysis</h5>
                            </div>
                            <div className="file-name-popup-body">
                              <label htmlFor="fileNameInput" className="file-name-label">Enter Analysis Name:</label>
                              <input
                                id="fileNameInput"
                                type="text"
                                className="file-name-input"
                                value={fileNamePopup.fileName}
                                onChange={(e) => setFileNamePopup(prev => ({ ...prev, fileName: e.target.value }))}
                                placeholder="Enter file name"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleProceed();
                                  } else if (e.key === 'Escape') {
                                    closeFileNamePopup();
                                  }
                                }}
                              />
                            </div>
                            <div className="file-name-popup-footer">
                              <button 
                                className="file-name-popup-btn cancel-btn" 
                                onClick={closeFileNamePopup}
                              >
                                Cancel
                              </button>
                              <button 
                                className="file-name-popup-btn proceed-btn" 
                                onClick={handleProceed}
                              >
                                Proceed
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </BatchExportQueueContext.Provider>
                    </PredictedPriceDataContext.Provider>
                  </SuggestedPriceContext.Provider>
                </SelectionContext.Provider>
              </FilteredInventoryContext.Provider>
            </AllStockDataContext.Provider>
            </AllInventoryDataContext.Provider>
          </AllSalesDataContext.Provider>
        </FilteredSalesContext.Provider>
      </PriceLinePercentContext.Provider>
    </MoveToPriceLineContext.Provider>
  );
};

export default ControlBar; 